import type {
  DashboardArchiveEntry,
  DashboardPreferenceScope,
  DashboardPreferences,
  DiscoveredGame,
  FavoriteGameEntry,
  ProjectCardAvailability,
} from '@volley/crucible-ipc-types/ipc-channels';

export interface DashboardPreferenceIdentity {
  scope: DashboardPreferenceScope;
  gameId: string;
  repoFullName?: string;
}

export interface LocalGamePreferenceSummary {
  gameId: string;
  placement?: 'local' | 'cloud';
  category?: string;
  heroImage?: string;
  description?: string;
  clonedFrom?: string;
  availability?: ProjectCardAvailability;
  disabledReason?: string;
}

function projectPreferenceScope(game: LocalGamePreferenceSummary): 'local' | 'cloud' {
  return game.placement === 'cloud' ? 'cloud' : 'local';
}

export interface DashboardFavoriteCard {
  key: string;
  scope: DashboardPreferenceScope;
  gameId: string;
  displayName: string;
  favoritedAt: string;
  source: 'local' | 'cloud' | 'community' | 'saved';
  repoFullName?: string;
  repoUrl?: string;
  heroImage?: string;
  description?: string;
  project?: {
    placement: 'local' | 'cloud';
    gameId: string;
    availability: ProjectCardAvailability;
    disabledReason?: string;
  };
}

export function dashboardPreferenceKey(identity: DashboardPreferenceIdentity): string {
  if (identity.scope === 'community') {
    const repoFullName = cleanOptionalString(identity.repoFullName);
    if (repoFullName) return `community:${repoFullName.toLowerCase()}`;
  }
  return `${identity.scope}:${identity.gameId}`;
}

export function isDashboardArchived(
  preferences: DashboardPreferences | undefined,
  identity: DashboardPreferenceIdentity,
): boolean {
  if (!preferences) return false;
  const key = dashboardPreferenceKey(identity);
  return preferences.archived.some((entry) => dashboardPreferenceKey(entry) === key);
}

export function isDashboardFavorited(
  preferences: DashboardPreferences | undefined,
  identity: DashboardPreferenceIdentity,
): boolean {
  if (!preferences) return false;
  const key = dashboardPreferenceKey(identity);
  return preferences.favorites.some((entry) => dashboardPreferenceKey(entry) === key);
}

export function filterDashboardLocalGames<T extends LocalGamePreferenceSummary>(
  games: T[],
  preferences: DashboardPreferences | undefined,
  options: {
    showTestGames: boolean;
    searchQuery: string;
    getDisplayName: (game: T) => string;
    isLocalGame?: (game: T) => boolean;
  },
): T[] {
  const query = options.searchQuery.trim().toLowerCase();
  return games
    .filter(
      (game) =>
        options.isLocalGame?.(game) === false ||
        !isDashboardArchived(preferences, {
          scope: projectPreferenceScope(game),
          gameId: game.gameId,
        }),
    )
    .filter((game) => options.showTestGames || !isTestGame(game))
    .filter((game) => !query || options.getDisplayName(game).toLowerCase().includes(query));
}

export function filterDashboardCommunityGames<T extends DiscoveredGame>(
  games: T[],
  preferences: DashboardPreferences | undefined,
  options: {
    localGameIds: string[];
    showTestGames: boolean;
    searchQuery: string;
  },
): T[] {
  const query = options.searchQuery.trim().toLowerCase();
  const localGameIds = new Set(options.localGameIds);
  const archivedLocalGameIds = new Set(
    (preferences?.archived ?? [])
      .filter((entry) => entry.scope === 'local')
      .map((entry) => entry.gameId),
  );
  return games
    .filter((game) => !localGameIds.has(game.gameId))
    .filter((game) => !archivedLocalGameIds.has(game.gameId))
    .filter(
      (game) =>
        !isDashboardArchived(preferences, {
          scope: 'community',
          gameId: game.gameId,
          repoFullName: game.repoFullName,
        }),
    )
    .filter((game) => options.showTestGames || !isTestGame(game))
    .filter((game) => !query || game.displayName.toLowerCase().includes(query));
}

export function countHiddenTestGames<T extends LocalGamePreferenceSummary>(
  games: T[],
  preferences: DashboardPreferences | undefined,
): number {
  return games.filter(
    (game) =>
      !isDashboardArchived(preferences, {
        scope: projectPreferenceScope(game),
        gameId: game.gameId,
      }) && isTestGame(game),
  ).length;
}

export function resolveDashboardFavorites<T extends LocalGamePreferenceSummary>(
  preferences: DashboardPreferences | undefined,
  options: {
    localGames: T[];
    communityGames: DiscoveredGame[];
    getLocalDisplayName: (game: T) => string;
    showTestGames: boolean;
  },
): DashboardFavoriteCard[] {
  if (!preferences) return [];
  const projectByKey = new Map(
    options.localGames.map((game) => [`${projectPreferenceScope(game)}:${game.gameId}`, game]),
  );
  const communityByKey = new Map(
    options.communityGames.map((game) => [
      dashboardPreferenceKey({
        scope: 'community',
        gameId: game.gameId,
        repoFullName: game.repoFullName,
      }),
      game,
    ]),
  );
  const archivedKeys = new Set(preferences.archived.map(dashboardPreferenceKey));

  return preferences.favorites.flatMap((favorite) => {
    const key = dashboardPreferenceKey(favorite);
    if (archivedKeys.has(key)) return [];

    if (favorite.scope === 'local' || favorite.scope === 'cloud') {
      const project = projectByKey.get(`${favorite.scope}:${favorite.gameId}`);
      if (project) {
        if (!options.showTestGames && isTestGame(project)) return [];
        return [favoriteFromLocal(favorite, project, options.getLocalDisplayName)];
      }
    }

    if (favorite.scope === 'community') {
      const community = communityByKey.get(key);
      if (community) {
        if (!options.showTestGames && isTestGame(community)) return [];
        return [favoriteFromCommunity(favorite, community)];
      }
    }

    if (!options.showTestGames && isTestGame({ gameId: favorite.gameId })) return [];
    return [favoriteFromSaved(favorite)];
  });
}

function favoriteFromLocal<T extends LocalGamePreferenceSummary>(
  favorite: FavoriteGameEntry,
  game: T,
  getDisplayName: (game: T) => string,
): DashboardFavoriteCard {
  const repoFullName = game.clonedFrom ?? favorite.repoFullName;
  return {
    key: dashboardPreferenceKey(favorite),
    scope: favorite.scope,
    gameId: game.gameId,
    displayName: getDisplayName(game),
    favoritedAt: favorite.favoritedAt,
    source: game.placement === 'cloud' ? 'cloud' : 'local',
    repoFullName,
    repoUrl: repoFullName ? `https://github.com/${repoFullName}` : favorite.repoUrl,
    heroImage: game.heroImage ?? favorite.heroImage,
    description: game.description ?? favorite.description,
    project: {
      placement: game.placement === 'cloud' ? 'cloud' : 'local',
      gameId: game.gameId,
      availability: game.availability ?? 'available',
      ...(game.disabledReason ? { disabledReason: game.disabledReason } : {}),
    },
  };
}

function favoriteFromCommunity(
  favorite: FavoriteGameEntry,
  game: DiscoveredGame,
): DashboardFavoriteCard {
  return {
    key: dashboardPreferenceKey(favorite),
    scope: favorite.scope,
    gameId: game.gameId,
    displayName: game.displayName,
    favoritedAt: favorite.favoritedAt,
    source: 'community',
    repoFullName: game.repoFullName,
    repoUrl: game.repoUrl,
    heroImage: game.heroImage ?? favorite.heroImage,
    description: game.description ?? favorite.description,
  };
}

function favoriteFromSaved(favorite: FavoriteGameEntry): DashboardFavoriteCard {
  return {
    key: dashboardPreferenceKey(favorite),
    scope: favorite.scope,
    gameId: favorite.gameId,
    displayName: favorite.displayName ?? favorite.gameId,
    favoritedAt: favorite.favoritedAt,
    source: 'saved',
    repoFullName: favorite.repoFullName,
    repoUrl: favorite.repoUrl,
    heroImage: favorite.heroImage,
    description: favorite.description,
  };
}

function isTestGame(game: { gameId: string; category?: string }): boolean {
  return game.category === 'test' || /^e2e[-_]?test/i.test(game.gameId);
}

function cleanOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
